import { useApiLoggingDemo } from "@/api/useDemo"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import type { Issue } from "@/domain/issue"
import {
  CACHE_TIME,
  Config,
  ConfigInfinityQuery,
  fetcher,
  onRetry,
  Params,
  STALE_TIME,
  useCreate,
  useDelete,
  useHttp,
  useLoadMore,
  useQueryClient,
  useUpdate
} from "@/hooks/useHttp"
import type { GetUseMutationOptions, QueryKeyT } from "@/hooks/useHttp.types"
import useServices from "@/hooks/useServices"
import { useQuery } from "@tanstack/react-query"
import { mockData } from "./useTriage.mockdata"

import type {
  AttackChainFeedback,
  AttackChainsResponse,
  FixesTriageResponse,
  IssuesTriageResponse,
  LinkTicketToIssuePayload,
  LinkTicketToIssueResponse,
  ProjectsDescriptionTriageResponse,
  ProjectsTriageResponse
} from "./useTriage.types"

import type { ListQuickWinsResponse } from "./useQuickWins.types"

export const useProjectsTriage = <T extends ProjectsTriageResponse>(
  params?: Params<Record<string, unknown>>,
  config?: Config<T>
) => {
  const context = useHttp<T>(RESOURCES_NAMES.TRIAGE.PROJECTS, params, config)

  return context
}

export const useProjectDescriptionTriage = <
  T extends ProjectsDescriptionTriageResponse
>(
  params: Params<{ needle: string }>,
  config?: Config<T>
) => {
  const { needle, ...restParams } = params
  const context = useHttp<T>(
    `${RESOURCES_NAMES.TRIAGE.PROJECTS}/${needle}`,
    restParams,
    { enabled: !!needle, ...config }
  )

  return context
}

export const useLoadMoreIssuesProjectTriage = <T = Issue>(
  params: Params<{ needle: string; pageNumber?: number | string }>,
  config: ConfigInfinityQuery<T>
) => {
  const { needle = "", pageNumber, ...restParams } = params

  const context = useLoadMore<T>(
    `${RESOURCES_NAMES.TRIAGE.PROJECTS}${needle}`,
    restParams,
    config
  )

  return context
}

export const useIssuesProjectTriage = <T extends IssuesTriageResponse>(
  params: Params<{ needle: string; pageNumber?: number | string }>,
  config?: Config<T>
) => {
  const { needle = "", ...restParams } = params

  const context = useHttp(
    `${RESOURCES_NAMES.TRIAGE.PROJECTS}${needle}`,
    restParams,
    config
  )

  return context
}

export const useIssuesByResourceTriage = <T extends IssuesTriageResponse>(
  params: Params<{
    resourceId?: string
    pageNumber?: number | string
    issueType?: string
  }>,
  config?: Config<T>
) => {
  const context = useHttp(`${RESOURCES_NAMES.TRIAGE.ISSUES}`, params, config)
  const nextContext = useApiLoggingDemo(context)

  if (params.issueType === "Vulnerable_Code,Exposed_Secret") return nextContext

  return context
}

export const useFiltersTriage = <
  T = KosmosApi.Response<Record<string, string[]>>
>(
  config: Config<T>
) => {
  const context = useHttp<T>(RESOURCES_NAMES.TRIAGE.FILTERS, undefined, config)

  return context
}

export const useDismissIssues = <T, S>(
  params: Record<string, unknown>,
  options?: GetUseMutationOptions<T, S>
) => {
  const context = useUpdate(
    RESOURCES_NAMES.TRIAGE.PROJECTS,
    params,
    undefined,
    options
  )
  return context
}

export const useDismissIssuesByFilter = <T, S>(
  params: Record<string, unknown>,
  options?: GetUseMutationOptions<T, S>
) => {
  const context = useUpdate(
    RESOURCES_NAMES.TRIAGE.ISSUES,
    params,
    undefined,
    options
  )
  return context
}

export const useCreateTicketIssue = () => {
  const context = useCreate<
    { data: Jira.CreateIssuePayload; needle?: string },
    Jira.CreateIssuePayload
  >(`${RESOURCES_NAMES.TRIAGE.ISSUES}`, {}, (oldData, _) => oldData)

  return context
}

export const useCalculateFixes = <T = FixesTriageResponse, S = unknown>() => {
  const context = useCreate<T, S>(`${RESOURCES_NAMES.TRIAGE.ISSUES}/fix`)

  return context
}

export const useIssue = <T = KosmosApi.Response<Issue>, S = T>(
  id: undefined | string,
  params?: Params,
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    `${RESOURCES_NAMES.TRIAGE.ISSUES}/${id}`,
    params,
    { enabled: !!id, ...config }
  )

  return context
}

// For actions that need an issue's project outside render; the keys match useIssue
// and useProjectDescriptionTriage, so their cache is reused
export const useFetchIssueAndProjectDescription = () => {
  const queryClient = useQueryClient()
  const services = useServices()

  const fetchQuery = <T>(queryKey: QueryKeyT) =>
    queryClient.fetchQuery<T, Error, T, QueryKeyT>({
      queryKey,
      queryFn: ({ queryKey, signal, client }) =>
        fetcher<T>({ queryKey, meta: undefined, signal, client }, services),
      gcTime: CACHE_TIME,
      staleTime: STALE_TIME,
      retry: onRetry
    })

  return {
    fetchIssue: (id: string) =>
      fetchQuery<IssuesTriageResponse>([
        `${RESOURCES_NAMES.TRIAGE.ISSUES}/${id}`,
        undefined
      ]),
    fetchProjectDescription: (needle: string) =>
      fetchQuery<ProjectsDescriptionTriageResponse>([
        `${RESOURCES_NAMES.TRIAGE.PROJECTS}/${needle}`,
        {}
      ])
  }
}

export const useAttackChains = <T = AttackChainsResponse, S = T>(
  issueId: undefined | string,
  params?: Params,
  config?: Config<T, S>
) => {
  const { auth } = useServices()
  const isDemo = auth.isDemo()
  const url = `${RESOURCES_NAMES.TRIAGE.ISSUES}/${issueId}/chains`
  // TODO: remove once we migrate demo data
  const { data, ...demoCtx } = useQuery<T>({
    queryKey: ["demo", url, params],
    queryFn: () => {
      if (!issueId) return null
      const attack = mockData[issueId]
      if (!attack) return null
      return {
        data: attack.scenarios,
        metadata: null,
        ok: true
      } as any
    },
    enabled: !!issueId && isDemo
  })
  // demo uses mockdata. until data is fully migrated, continue service the mock data
  const context = useHttp<T, S>(
    `${RESOURCES_NAMES.TRIAGE.ISSUES}/${issueId}/chains`,
    params,
    { enabled: !!issueId && !isDemo, ...config }
  )

  return isDemo ? { ...demoCtx, response: data } : context
}

export const useAttackChainFeedback = <
  T = { needle: string },
  S = { data: { feedback: AttackChainFeedback } }
>(
  params?: Record<string, unknown>,
  options?: GetUseMutationOptions<T, S>
) => {
  const context = useUpdate<T, S>(
    RESOURCES_NAMES.TRIAGE.CHAINS,
    params,
    undefined,
    options
  )
  return context
}

export const useQuickWins = <
  T extends ListQuickWinsResponse = ListQuickWinsResponse,
  S = T
>(
  params?: Params,
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    RESOURCES_NAMES.TRIAGE.QUICK_WINS.ROOT,
    params,
    config
  )

  return context
}

export const useCreateLinkTicketToIssue = (
  options?: GetUseMutationOptions<
    LinkTicketToIssueResponse,
    LinkTicketToIssuePayload
  >
) => {
  const context = useCreate<
    LinkTicketToIssueResponse,
    LinkTicketToIssuePayload
  >(RESOURCES_NAMES.TRIAGE.ISSUES, undefined, undefined, options)

  return context
}

export const useDeleteLinkTicketToIssue = (
  options?: GetUseMutationOptions<
    LinkTicketToIssueResponse,
    LinkTicketToIssuePayload
  >
) => {
  const context = useDelete<
    LinkTicketToIssueResponse,
    LinkTicketToIssuePayload
  >(RESOURCES_NAMES.TRIAGE.ISSUES, undefined, undefined, options)

  return context
}
